import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import Caption from 'LuxKit/Typography/Caption'
import React, { FocusEventHandler, useCallback, useEffect, useMemo } from 'react'
import RecentSearchSuggestions from '../SearchForm/SearchLocationInput/RecentSearchSuggestions'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import SearchPlaceSelectList from './SearchPlaceSelectList'
import styled from 'styled-components'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import TextAreaOnly from 'components/Common/Form/Input/TextAreaOnly'
import IconButton from 'LuxKit/Button/IconButton'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import {
  LOCATION_SEARCH_ERROR_MESSAGE,
} from 'constants/search'
import { rem } from 'polished'
import { TypeaheadActionType } from 'analytics/snowplow/events'
import SearchTextListItem from '../SearchTextListItem/SearchTextListItem'
const PlaceControl = styled(VerticalSpacer)`
  position: relative;
  width: 100%;
  height: ${rem(46)};
`

const SearchItemContainer = styled.div`
  margin-top: ${rem(8)};
`

interface Props {
  className?: string
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  value?: App.SearchItem
  required?: boolean
  placeholder: string
  localInputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>
  searchPhrase: string
  onClearClick: () => void
  finalOpen: boolean
  showPlaceholder: boolean
  hasRecentSearchItems: boolean
  recentSearches: Array<App.FullRecentSearch>
  selectedDropdown: number | undefined
  onRecentSearchDelete: (item: App.FullRecentSearch) => void
  onRecentSearchClick: (item: App.FullRecentSearch) => void
  placeholderTitle?: string
  onLocationSelect: (
    searchItem?: App.SearchItem,
    mouseEvent?: React.MouseEvent<HTMLElement>,
    actionType?: TypeaheadActionType,
  ) => void
  placeTypes?: Array<App.SearchPlaceType>
  searchTypes?: Array<App.SearchType>
  searchVertical?: App.SearchPlaceVertical
  overrideSearchItems?: Array<App.SearchItem>
  setSearchPhrase: React.Dispatch<React.SetStateAction<string>>
  placeholderItems: Array<App.SearchItem>
  placeholdersSearches: Array<App.SearchItem>
  handleResultsChange?: (
    results: Array<App.SearchItem>,
    searchPhrase: string,
  ) => void
  showHotelImagesLayout?: boolean
  multiline?: boolean
  showNaturalLanguageSearchSuggestions?: boolean
}

function SearchPlaceSelectInline(props: Props) {
  const {
    className,
    onKeyDown,
    value,
    required,
    placeholder,
    localInputRef,
    searchPhrase,
    showPlaceholder,
    hasRecentSearchItems,
    recentSearches,
    selectedDropdown,
    onRecentSearchDelete,
    onRecentSearchClick,
    placeholderTitle,
    onLocationSelect,
    placeTypes,
    searchTypes,
    searchVertical,
    overrideSearchItems,
    setSearchPhrase,
    onClearClick,
    placeholderItems,
    handleResultsChange,
    multiline = false,
    showNaturalLanguageSearchSuggestions = false,
  } = props

  const handleOnClick = useCallback((searchItem?: App.SearchItem) => {
    if (!searchItem) {
      onLocationSelect(searchItem)
      return
    }

    const itemIndex = placeholderItems.findIndex(item => item.value === searchItem.value)
    if (itemIndex === -1) {
      onLocationSelect(searchItem)
      return
    }

    onLocationSelect(searchItem, undefined, undefined)
  }, [onLocationSelect, placeholderItems])

  useEffect(() => {
    localInputRef.current?.focus({ preventScroll: true })
  }, [localInputRef])

  const handleFocus = useCallback<FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>((event) => {
    if (event.currentTarget.value) {
      event.currentTarget.select()
    }
  }, [])

  const naturalLanguageResults = useMemo(() => {
    return placeholderItems.filter(item => item.searchType === 'naturalLanguage')
  }, [placeholderItems])

  const nonNaturalLanguageResults = useMemo(() => {
    return placeholderItems.filter(item => item.searchType !== 'naturalLanguage')
  }, [placeholderItems])

  return (<PlaceControl gap={12} className={className}>
    {!!required && <HiddenInput
      value={value ? 'filled' : undefined}
      required
      invalidMessage={LOCATION_SEARCH_ERROR_MESSAGE}
    />}
    {multiline && (
      <TextAreaOnly
        noValidationMessage
        noValidationSpacing
        resize="none"
        placeholder={placeholder}
        onFocus={handleFocus}
        ref={localInputRef as React.RefObject<HTMLTextAreaElement>}
        defaultValue={value?.format.mainText}
        onChange={e => setSearchPhrase(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        endIcon={searchPhrase.length > 0 ?
            <IconButton kind="tertiary" onClick={onClearClick}><LineTimesIcon /></IconButton> :
            <LineSearchIcon />}
      />
    )}
    {!multiline && (
      <TextInputOnly
        noValidationSpacing
        placeholder={placeholder}
        onFocus={handleFocus}
        ref={localInputRef as React.RefObject<HTMLInputElement>}
        defaultValue={value?.format.mainText}
        onChange={e => setSearchPhrase(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        endIcon={searchPhrase.length > 0 ?
            <IconButton kind="tertiary" onClick={onClearClick}><LineTimesIcon /></IconButton> :
            <LineSearchIcon />}
      />
    )}
    {searchVertical === 'hotel' &&
      <>
        {showPlaceholder && <VerticalSpacer gap={12}>
          {hasRecentSearchItems && <RecentSearchSuggestions
            recentSearches={recentSearches}
            onDelete={onRecentSearchDelete}
            selectedDropdown={selectedDropdown}
            onRecentSearchClick={onRecentSearchClick}
            iconBackground
            horizontal={false}
          />}
          {showNaturalLanguageSearchSuggestions && naturalLanguageResults.length > 0 &&
            <>
              <Caption variant="large" weight="bold">Suggested searches</Caption>
              {naturalLanguageResults.map(
                (suggestion, index) =>
                  <SearchTextListItem
                    key={`${suggestion.value}_${index}`}
                    text={suggestion.value}
                    onClick={handleOnClick}
                  />
              )}
            </>}
          {placeholderTitle && <Caption variant="large" weight="bold">{placeholderTitle}</Caption>}
          <SearchItemContainer>
            {nonNaturalLanguageResults.map(item => <SearchMenuListItem
              key={item.value}
              searchItem={item}
              onClick={handleOnClick}
              imageId={item.imageId}
              searchVertical={searchVertical}
              useImage
            />)}
          </SearchItemContainer>
        </VerticalSpacer>}
        {!showPlaceholder && <SearchPlaceSelectList
          searchPhrase={searchPhrase}
          onResultsChange={handleResultsChange}
          onChange={onLocationSelect}
          placeTypes={placeTypes}
          searchTypes={searchTypes}
          vertical={searchVertical}
          overrideSearchItems={overrideSearchItems}
          useImage
          showNaturalLanguageSearchSuggestions={showNaturalLanguageSearchSuggestions}
        />}
      </>}
    {searchVertical !== 'hotel' &&
      <>
        {showPlaceholder && <VerticalSpacer gap={12}>
          {hasRecentSearchItems && <>
            <RecentSearchSuggestions
              recentSearches={recentSearches}
              onDelete={onRecentSearchDelete}
              selectedDropdown={selectedDropdown}
              onRecentSearchClick={onRecentSearchClick}
            />
            <Divider kind="primary" />
          </>}
          {placeholderTitle && <Caption variant="large" weight="bold">{placeholderTitle}</Caption>}
          <div>
            {placeholderItems.map(item => <SearchMenuListItem
              key={item.value}
              searchItem={item}
              onClick={handleOnClick}
              searchVertical={searchVertical}
              useImage={searchVertical === 'tour'}
            />)}
          </div>
        </VerticalSpacer>}
        {!showPlaceholder && <SearchPlaceSelectList
          searchPhrase={searchPhrase}
          onResultsChange={handleResultsChange}
          onChange={onLocationSelect}
          placeTypes={placeTypes}
          searchTypes={searchTypes}
          vertical={searchVertical}
          overrideSearchItems={overrideSearchItems}
          useImage={searchVertical === 'tour'}
        />}
      </>}
  </PlaceControl>
  )
}

export default SearchPlaceSelectInline
